import { useFormState, type Control, type FieldValues, type Path } from 'react-hook-form';

import { flattenErrorPaths } from '../error-paths/error-paths';
import { fieldColSpan } from '../resolve-field-layout/resolve-field-layout';
import {
  type FieldColSpan,
  type FormColumns,
  type ObjectFieldDef,
} from '../types/dynamic-form.types';

export interface UseObjectFieldGroupLogicInput<TValues extends FieldValues> {
  field: ObjectFieldDef;
  control: Control<TValues>;
  /** The enclosing section's ceiling, which the group's own span is clamped to. */
  sectionColumns: FormColumns;
}

export interface UseObjectFieldGroupLogicResult {
  /** Something under the group was rejected — said in the legend. */
  isInvalid: boolean;
  /** The ceiling for the group's OWN grid, under its own container's ramp. */
  innerColumns: FormColumns;
  /** How many of the enclosing section's tracks the group takes. */
  cellColSpan: FieldColSpan;
}

/**
 * What an object group knows about itself.
 *
 * **The subscription is narrow on purpose.** `useFormState({ name })` re-renders
 * this group only when one of the named paths changes its error state, so a
 * typo in a field on the other side of the form does not re-render every group
 * on screen. Reading `formState.errors` from the whole form would.
 *
 * The group is marked from **its own path as well as its properties'**: a rule
 * about the object as a whole (a refinement, a required property that is absent
 * entirely) fails at `value`, not at `value.address`, and a legend that stayed
 * quiet for those would leave the operator with a Save that does nothing and
 * nothing on screen to read.
 */
export function useObjectFieldGroupLogic<TValues extends FieldValues>({
  field,
  control,
  sectionColumns,
}: UseObjectFieldGroupLogicInput<TValues>): UseObjectFieldGroupLogicResult {
  const watched = [
    field.name,
    ...field.properties.map((property) => property.name),
  ] as Path<TValues>[];
  const { errors } = useFormState({ control, name: watched });

  const rejected = flattenErrorPaths(errors).map((error) => error.path);
  const isInvalid = rejected.some(
    // A SEGMENT-boundary match, never `startsWith`: `valueOther` begins with
    // `value` and has nothing to do with this group.
    (path) => path === field.name || path.startsWith(`${field.name}.`),
  );

  return {
    isInvalid,
    // One column until the group says otherwise. The section's ceiling describes
    // the row the group sits IN, and says nothing about the inside of one cell.
    innerColumns: field.columns ?? 1,
    cellColSpan: fieldColSpan(field, sectionColumns),
  };
}
